import {
  ArrowLeftIcon,
  BasketIcon,
  CaretUpIcon,
  DownloadSimpleIcon,
  EyeClosedIcon,
  EyeIcon,
  GearIcon,
  LinkIcon,
  PlusIcon,
  ShoppingBagIcon,
  TrashIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { useDeleteMarketShopInventoryItem } from '@/api/market-shop/inventory/delete-market-shop-inventory-item'
import { useExportMarketShopInventory } from '@/api/market-shop/inventory/export-market-shop-inventory'
import { useGetMarketShopInventoryItem } from '@/api/market-shop/inventory/get-market-shop-inventory-item'
import { useHideMarketShopInventoryItem } from '@/api/market-shop/inventory/hide-market-shop-inventory-item'
import { useUnhideMarketShopInventoryItem } from '@/api/market-shop/inventory/unhide-market-shop-inventory-item'
import { useUploadMarketShopInventory } from '@/api/market-shop/inventory/upload-market-shop-inventory'
import { useGetMarketShopLink } from '@/api/market-shop/settings/get-market-shop-link'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { useFilePicker } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'
import { downloadHref } from '@/utils/file'

import {
  INVENTORY_TEMPLATE_PATH,
  MAX_UPLOAD_FILE_SIZE,
} from './market-shop-dashboard.page.constants'
import {
  getRestaurantMarketShopLink,
  isProductHidden,
  isProductOutOfStock,
} from './market-shop-dashboard.page.utils'
import * as S from './market-shop-dashboard.styled'

export const MarketShopDashboardPage = () => {
  const { t } = useTranslation(['market-shop-dashboard', 'common'])
  const { navigate, navigateBack, useMatch } = routerFactory.useRouter()
  const isSettingsPath = useMatch({
    path: '/dashboard/building-dashboard/market-shop/settings',
  })
  const isAddProductPath = useMatch({
    path: '/dashboard/building-dashboard/market-shop/inventory/add',
  })
  const isEditProductPath = useMatch({
    path: '/dashboard/building-dashboard/market-shop/inventory/:id',
  })

  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id

  const editProductId = isEditProductPath?.params?.id
    ? Number(isEditProductPath.params.id)
    : undefined
  const { data: editProduct, refetch: refetchEditProduct } =
    useGetMarketShopInventoryItem(editProductId)
  const { mutateAsync: hideItem, isPending: isHiding } = useHideMarketShopInventoryItem()
  const { mutateAsync: unhideItem, isPending: isUnhiding } = useUnhideMarketShopInventoryItem()
  const { mutateAsync: deleteItem, isPending: isDeleting } = useDeleteMarketShopInventoryItem()

  const { data: marketShopLinks } = useGetMarketShopLink({ buildingId })
  const marketShopLink = marketShopLinks?.[0]?.link
  const { mutateAsync: uploadInventory, isPending: isUploading } = useUploadMarketShopInventory()
  const { mutateAsync: downloadInventory, isPending: isDownloading } =
    useExportMarketShopInventory()

  const handleFileUpload = async (files: File[]) => {
    const file = files[0]
    if (!file || !buildingId) return

    try {
      const result = await uploadInventory({ buildingId, file })
      toastFactory.success(
        t('market-shop-dashboard:dashboard.uploadSuccess', {
          inserted: result.inserted,
          skipped: result.skipped,
        }),
      )
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const { inputProps, open: openFilePicker } = useFilePicker({
    acceptedTypes: ['.csv'],
    maxFileSize: MAX_UPLOAD_FILE_SIZE,
    maxFiles: 1,
    onFilesSelected: handleFileUpload,
    onValidationError: (errors) =>
      toastFactory.error(errors[0]?.errors[0] ?? t('market-shop-dashboard:dashboard.invalidFile')),
  })

  const handleCopyMarketOrders = async () => {
    if (!marketShopLink) {
      toastFactory.error(t('market-shop-dashboard:dashboard.noLinkAvailable'))
      return
    }

    await navigator.clipboard.writeText(getRestaurantMarketShopLink(marketShopLink))
    toastFactory.success(t('market-shop-dashboard:dashboard.marketOrdersLinkCopied'))
  }

  const handleCopyMarketShop = async () => {
    if (!marketShopLink) {
      toastFactory.error(t('market-shop-dashboard:dashboard.noLinkAvailable'))
      return
    }

    await navigator.clipboard.writeText(marketShopLink)
    toastFactory.success(t('market-shop-dashboard:dashboard.marketShopLinkCopied'))
  }

  const handleDownloadTemplate = () => {
    downloadHref(INVENTORY_TEMPLATE_PATH, 'Market_Shop_Inventory_Template.csv')
  }

  const handleHide = () => {
    if (!editProductId || !buildingId) return

    popupFactory.open(ConfirmationPopup, {
      children: t('market-shop-dashboard:inventory.rowActions.hide.confirmBody', {
        summary: editProduct?.summary ?? '',
      }),
      confirmIcon: EyeClosedIcon,
      confirmText: t('market-shop-dashboard:inventory.bulkActions.hide.label'),
      onConfirm: async () => {
        await hideItem({ buildingId, id: editProductId })
        void refetchEditProduct()
      },
      title: t('market-shop-dashboard:inventory.bulkActions.hide.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleUnhide = () => {
    if (!editProductId || !buildingId) return

    popupFactory.open(ConfirmationPopup, {
      children: t('market-shop-dashboard:inventory.rowActions.unhide.confirmBody', {
        summary: editProduct?.summary ?? '',
      }),
      confirmIcon: EyeIcon,
      confirmText: t('market-shop-dashboard:inventory.bulkActions.unhide.label'),
      onConfirm: async () => {
        await unhideItem({ buildingId, id: editProductId })
        void refetchEditProduct()
      },
      title: t('market-shop-dashboard:inventory.bulkActions.unhide.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleDeleteProduct = () => {
    if (!editProductId || !buildingId) return

    popupFactory.open(ConfirmationPopup, {
      children: t('market-shop-dashboard:dashboard.editProduct.deleteConfirmation.body', {
        summary: editProduct?.summary ?? '',
      }),
      confirmText: t('common:actions.delete'),
      onConfirm: async () => {
        if (!buildingId || !editProductId) return
        await deleteItem({ buildingId, id: editProductId })
        navigateBack({ path: '/dashboard/building-dashboard/market-shop/inventory' })
      },
      title: t('market-shop-dashboard:dashboard.editProduct.deleteConfirmation.title'),
      variant: 'danger',
    })
  }

  const handleDownloadInventory = () => {
    if (!buildingId || !activeBuilding) return

    const trimmed = activeBuilding.name?.trim()
    const fileName = trimmed ? `${trimmed}-market_shop_inventory.csv` : 'market_shop_inventory.csv'

    void downloadInventory({ buildingId, fileName })
  }

  return (
    <Page>
      {isSettingsPath ? (
        <PageHeader
          title={t('market-shop-dashboard:settings.title')}
          backButton={
            <Link
              navigateBack
              use="secondary"
              size="small"
              to={{ path: '/dashboard/building-dashboard/market-shop' }}
            >
              <Icon icon={ArrowLeftIcon} />
              {t('market-shop-dashboard:settings.backButton')}
            </Link>
          }
        />
      ) : isAddProductPath ? (
        <PageHeader
          title={t('market-shop-dashboard:dashboard.addProduct.title')}
          backButton={
            <Link
              navigateBack
              use="secondary"
              size="small"
              to={{ path: '/dashboard/building-dashboard/market-shop/inventory' }}
            >
              <Icon icon={ArrowLeftIcon} />
              {t('common:actions.back')}
            </Link>
          }
        />
      ) : isEditProductPath ? (
        <PageHeader
          title={t('market-shop-dashboard:dashboard.editProduct.title')}
          backButton={
            <Link
              navigateBack
              use="secondary"
              size="small"
              to={{ path: '/dashboard/building-dashboard/market-shop/inventory' }}
            >
              <Icon icon={ArrowLeftIcon} />
              {t('common:actions.back')}
            </Link>
          }
          actions={
            <>
              <Button
                use="secondary"
                size="small"
                disabled={isHiding || isUnhiding}
                loading={isDeleting}
                onClick={handleDeleteProduct}
              >
                <Icon icon={TrashIcon} color="red500" />
                {t('common:actions.delete')}
              </Button>
              <Button
                use="secondary"
                size="small"
                disabled={isDeleting || isProductOutOfStock(editProduct)}
                loading={isHiding || isUnhiding}
                onClick={
                  isProductHidden(editProduct) || isProductOutOfStock(editProduct)
                    ? handleUnhide
                    : handleHide
                }
              >
                <Icon
                  icon={
                    isProductHidden(editProduct) || isProductOutOfStock(editProduct)
                      ? EyeClosedIcon
                      : EyeIcon
                  }
                />
                {isProductHidden(editProduct) || isProductOutOfStock(editProduct)
                  ? t('market-shop-dashboard:dashboard.editProduct.unhideButton')
                  : t('market-shop-dashboard:dashboard.editProduct.hideButton')}
              </Button>
            </>
          }
        />
      ) : (
        <PageHeader
          title={t('market-shop-dashboard:dashboard.title')}
          navbarItems={[
            {
              label: t('market-shop-dashboard:dashboard.navbarItems.inventory'),
              to: { path: '/dashboard/building-dashboard/market-shop/inventory' },
            },
            {
              label: t('market-shop-dashboard:dashboard.navbarItems.orders'),
              to: { path: '/dashboard/building-dashboard/market-shop/orders' },
            },
          ]}
          actions={
            <>
              <DropdownMenu
                items={[
                  {
                    // Note: If market shop restaurant is not available, then market orders (restaurant management) is not shown
                    hidden: !activeBuilding?.marketShopRestaurantAvailable,
                    icon: BasketIcon,
                    label: t('market-shop-dashboard:dashboard.copyLinkMenu.options.marketOrders'),
                    onClick: handleCopyMarketOrders,
                  },
                  {
                    // Note: For restaurant market shop, this link is just shown for the PM. It can display the products, but cannot be used to place orders.
                    icon: ShoppingBagIcon,
                    label: t('market-shop-dashboard:dashboard.copyLinkMenu.options.marketShop'),
                    onClick: handleCopyMarketShop,
                  },
                ]}
                trigger={({ isOpen }) => (
                  <S.CopyLinkButton use="secondary" size="small">
                    <Icon icon={LinkIcon} />
                    {t('market-shop-dashboard:dashboard.copyLinkMenu.trigger')}
                    <S.CaretIcon icon={CaretUpIcon} $isOpen={isOpen} />
                  </S.CopyLinkButton>
                )}
              />
              <DropdownMenu
                items={[
                  {
                    label: t(
                      'market-shop-dashboard:dashboard.downloadMenu.options.inventoryTemplate',
                    ),
                    onClick: handleDownloadTemplate,
                  },
                  {
                    label: t(
                      'market-shop-dashboard:dashboard.downloadMenu.options.currentInventory',
                    ),
                    onClick: handleDownloadInventory,
                  },
                ]}
                trigger={({ isOpen }) => (
                  <S.DownloadButton use="secondary" size="small" loading={isDownloading}>
                    <Icon icon={DownloadSimpleIcon} />
                    {t('market-shop-dashboard:dashboard.downloadMenu.trigger')}
                    <S.CaretIcon icon={CaretUpIcon} $isOpen={isOpen} />
                  </S.DownloadButton>
                )}
              />
              <Link
                size="small"
                to={{ path: '/dashboard/building-dashboard/market-shop/settings' }}
                use="secondary"
              >
                <Icon icon={GearIcon} />
                {t('market-shop-dashboard:dashboard.marketSetupLink')}
              </Link>
              <DropdownMenu
                items={[
                  {
                    icon: PlusIcon,
                    label: t('market-shop-dashboard:dashboard.addItemMenu.options.addNewItem'),
                    onClick: () =>
                      navigate({
                        path: '/dashboard/building-dashboard/market-shop/inventory/add',
                      }),
                  },
                  {
                    icon: UploadSimpleIcon,
                    label: t('market-shop-dashboard:dashboard.uploadInventory'),
                    onClick: openFilePicker,
                  },
                ]}
                trigger={({ isOpen }) => (
                  <Button size="small" loading={isUploading}>
                    {t('market-shop-dashboard:dashboard.addItemMenu.trigger')}
                    <S.CaretIcon icon={CaretUpIcon} $isOpen={isOpen} />
                  </Button>
                )}
              />
              <S.HiddenInput {...inputProps} />
            </>
          }
        />
      )}
      <Outlet context={{ isDeleting }} />
    </Page>
  )
}
